import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../layouts/PageLayout.js';
import { getLatestBuzz, getBuzzSEOMetadata, getBuzzHashtags, getSocialLinks } from '../services/BuzzService.js';
import logo from '../assets/logo.png';

function BuzzPage() {
  const [buzzData, setBuzzData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seoMetadata = getBuzzSEOMetadata();
  const hashtags = getBuzzHashtags();
  const socialLinks = getSocialLinks();

  useEffect(() => {
    loadBuzzContent();
  }, []);

  const loadBuzzContent = async () => {
    try {
      const data = await getLatestBuzz();
      setBuzzData(data);
    } catch (error) {
      console.error('Error loading buzz content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div style={{ padding: '4rem 0', textAlign: 'center', color: '#fff' }}>
          <p>Loading latest industry buzz...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>{seoMetadata.title}</title>
        <meta name="description" content={seoMetadata.description} />
        <meta name="keywords" content={seoMetadata.keywords} />
        <meta property="og:title" content={seoMetadata.title} />
        <meta property="og:description" content={seoMetadata.description} />
        <meta property="og:url" content={`https://decodedmusic.com${seoMetadata.url}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoMetadata.title} />
        <meta name="twitter:description" content={seoMetadata.description} />
        <link rel="canonical" href={`https://decodedmusic.com${seoMetadata.url}`} />
      </Helmet>
      
      <section style={{ background: 'linear-gradient(135deg, #111827 0%, #1e293b 100%)', color: '#fff', padding: '2rem 0 1rem 0', borderBottom: '2px solid #2563eb', boxShadow: '0 8px 32px #0004' }}>
        <div style={{ maxWidth: '44rem', margin: '0 auto', textAlign: 'center' }}>
          <img src={logo} alt="Decoded Music Industry Buzz - sync licensing insights" style={{ width: 100, marginBottom: 24, filter: 'drop-shadow(0 2px 12px #2563eb88)' }} />
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 16, letterSpacing: '0.01em', textShadow: '0 4px 32px #2563eb55, 0 2px 0 #fff2' }}>
            Welcome to <span style={{ color: '#2563eb', textShadow: '0 2px 16px #2563eb99' }}>DECODED</span> — AI Music, Sync Licensing & Catalog Intelligence
          </h1>
          <p style={{ fontSize: '1.35rem', marginBottom: 16, color: '#e0e7ef', fontWeight: 500 }}>
            Weekly roundup of sync opportunities, catalog deals, AI music trends, and rights enforcement across LA, NY, Atlanta, and Miami. We analyze what makes tracks go viral, sync licensing wins, and how <span style={{ color: '#fff', fontWeight: 700 }}>YOU</span> can position your music for playlists, brands, and streaming success.
          </p>
          <div style={{ fontSize: '1.15rem', margin: '1.5rem 0', textAlign: 'left', display: 'inline-block', background: '#222b3a', borderRadius: 12, padding: '1.2rem 2rem', boxShadow: '0 2px 12px #2563eb33' }}>
            <span role="img" aria-label="headphones">🎧</span> <b>Featuring:</b>
            <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0, textAlign: 'left', color: '#e0e7ef' }}>
              <li>AI music and sync licensing trends</li>
              <li>Catalog acquisition opportunities</li>
              <li>Spotify playlist strategies & songs of summer</li>
              <li>Music rights enforcement updates</li>
              <li>Independent artist growth insights</li>
            </ul>
          </div>
          <p style={{ fontSize: '1.15rem', marginBottom: 12, color: '#e0e7ef' }}>
            If you're an artist, manager, or label exec looking to crack the code on music sync and streaming success, subscribe and stay locked in.
          </p>
          <div style={{ fontSize: '1rem', color: '#60a5fa', marginBottom: 0, fontWeight: 500 }}>
            <p style={{ margin: '0.5rem 0' }}>
              📲 <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{socialLinks.label}</a>
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              🎵 <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>{socialLinks.playlistLabel}</a>
            </p>
          </div>
        </div>
      </section>

      <section style={{ background: 'linear-gradient(135deg, #1e293b 0%, #111827 100%)', color: '#fff', padding: '2.5rem 0' }}>
        <div style={{ maxWidth: '44rem', margin: '0 auto' }}>
          <header style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#2563eb', letterSpacing: '0.01em', textShadow: '0 2px 16px #2563eb99' }}>
              This Week's Industry Buzz 
              <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: '1.2rem' }}> [AI Music & Sync Licensing]</span>
            </h2>
            <p style={{ fontSize: '1.15rem', color: '#93c5fd', marginBottom: 0, fontWeight: 500 }}>
              Curated by Decoded's Music Intelligence Team
            </p>
            {buzzData && (
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Last updated: {new Date(buzzData.lastUpdated).toLocaleDateString()} | Source: {buzzData.source}
              </p>
            )}
          </header>
          
          <div style={{ background: '#222b3a', color: '#e0e7ef', padding: '2rem', borderRadius: 16, lineHeight: 1.8, boxShadow: '0 2px 16px #2563eb22', fontFamily: 'Georgia, serif', fontWeight: 500 }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '1.25rem', margin: 0, fontFamily: 'inherit' }}>
              {buzzData ? buzzData.content : 'Loading industry intelligence...'}
            </pre>
          </div>

          <div style={{ marginTop: '2rem', padding: '1rem', background: '#1a202c', borderRadius: 8, textAlign: 'center' }}>
            <p style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '0.5rem' }}>Share this buzz:</p>
            <div style={{ fontSize: '0.85rem', color: '#60a5fa' }}>
              {hashtags.join(' ')}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

export default BuzzPage;
